/**
 * PurchaseVerifyingOverlay
 * 
 * Shows a non-dismissable "Verifying your purchase…" loader when the user
 * returns to the app from a Google Play / App Store billing flow. Without
 * this, when a user hits the back button mid-payment (or after UPI redirect),
 * the app instantly shows the dashboard with stale credits — they only update
 * 5-10 seconds later after the IAP plugin replays the approved transaction
 * and /api/iap/verify completes. That UX feels broken ("did my payment fail?").
 * 
 * Logic:
 *   - On purchaseProduct() invocation, iapService sets `iap_purchase_in_flight`
 *     in localStorage (survives app backgrounding for UPI redirect).
 *   - When app regains focus / becomes visible, this component checks the flag.
 *   - Shows overlay until either:
 *       a) `iap-result` event fires (verify completed — success or fail)
 *       b) 60 seconds elapsed (timeout fallback)
 *   - On dismiss, refreshes session so credits balance shows authoritative value.
 */
import React, { useEffect, useRef, useState } from 'react';
import { rlog } from '../utils/railwayLogger';

const POLL_INTERVAL = 1500;
const TIMEOUT_MS = 60000;
// If localStorage flag is older than this, treat as stale and clear immediately.
// Set BELOW the overlay timeout so we never poll on a flag that should already
// have been cleared by /verify or restorePurchases (purchase verify takes <90s
// in worst case, after which iapService removes the flag itself).
const STALE_FLAG_MS = 5 * 60 * 1000;

const PurchaseVerifyingOverlay = ({ onComplete }) => {
  const [visible, setVisible] = useState(false);
  const [secs, setSecs] = useState(0);
  // Refs so multiple visibility/focus fires don't stack timers.
  const timerRef = useRef(null);
  const startedAtRef = useRef(null);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const checkInFlight = () => {
      try {
        const raw = localStorage.getItem('iap_purchase_in_flight');
        if (!raw) return false;
        const flag = JSON.parse(raw);
        // Auto-clear stale flag (>5 mins old) — purchase verify always
        // completes well before this in production.
        if (Date.now() - (flag.started_at || 0) > STALE_FLAG_MS) {
          localStorage.removeItem('iap_purchase_in_flight');
          return false;
        }
        return true;
      } catch {
        // Corrupt JSON → clear and bail.
        try { localStorage.removeItem('iap_purchase_in_flight'); } catch {}
        return false;
      }
    };

    const dismiss = (reason, elapsed) => {
      clearTimer();
      startedAtRef.current = null;
      setVisible(false);
      rlog('purchase_verifying_overlay_dismissed', { elapsed_ms: elapsed, reason });
      if (onComplete) onComplete();
    };

    const showOverlay = () => {
      if (!checkInFlight()) {
        // Flag absent or stale — make sure overlay is hidden and bail.
        if (visible) setVisible(false);
        return;
      }
      // Idempotent: if a timer is already running, don't start another.
      if (timerRef.current) return;

      setVisible(true);
      startedAtRef.current = Date.now();
      rlog('purchase_verifying_overlay_shown', {});

      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - (startedAtRef.current || Date.now());
        setSecs(Math.floor(elapsed / 1000));
        if (!checkInFlight()) {
          dismiss('flag_cleared', elapsed);
          return;
        }
        if (elapsed >= TIMEOUT_MS) {
          // Hard stop: nuke the flag so we don't loop forever on stale data.
          try { localStorage.removeItem('iap_purchase_in_flight'); } catch {}
          dismiss('timeout', elapsed);
        }
      }, POLL_INTERVAL);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Small delay so cordova-plugin-purchase can replay approved txn first.
        setTimeout(showOverlay, 300);
      }
    };
    const onFocus = () => setTimeout(showOverlay, 300);
    const resultHandler = () => {
      // /verify finished — wipe flag and hide instantly.
      try { localStorage.removeItem('iap_purchase_in_flight'); } catch {}
      const elapsed = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
      dismiss('iap_result_event', elapsed);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    window.addEventListener('iap-result', resultHandler);

    // Initial mount check.
    showOverlay();

    return () => {
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('iap-result', resultHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!visible) return null;

  return (
    <div
      data-testid="purchase-verifying-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        background: 'rgba(8, 12, 28, 0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        color: '#F3E5AB',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: '64px',
          height: '64px',
          border: '3px solid rgba(243, 229, 171, 0.2)',
          borderTopColor: '#F3E5AB',
          borderRadius: '50%',
          animation: 'taraanaya-spin 1s linear infinite',
          marginBottom: '24px',
        }}
      />
      <div style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>
        Verifying your purchase…
      </div>
      <div style={{ fontSize: '14px', opacity: 0.85, maxWidth: '320px', lineHeight: 1.5 }}>
        Aapka payment process ho raha hai. Please app band na karein —
        credits jald hi update honge.
      </div>
      {secs > 5 && (
        <div style={{ marginTop: '20px', fontSize: '12px', opacity: 0.6 }}>
          {secs}s elapsed
        </div>
      )}
      <style>{`
        @keyframes taraanaya-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PurchaseVerifyingOverlay;
